import {derived, get, writable} from 'svelte/store';
import {groupBy} from 'lodash';
import {
  getIncentiveTiers,
  getInvestorIncentiveTiers,
} from 'Utility/deals/incentive_tier/getIncentiveTiers';
import type {
  GroupedTierEntries,
  GroupedTiers,
  IncentiveTier,
  IncentiveTiersResponse,
  TierType,
} from 'Types/incentiveTier/incentiveTierTypes';
import {incentiveTiersEnabled} from 'Utility/deals/incentive_tier/flags';
import type {DealFeatureFlags} from 'Types/deals/dealFeatureFlags';
import {camelizeKeys} from 'Utility/commons/transformKeys';

export const incentiveTierStore = writable<IncentiveTiersResponse>({items: []});
export const investorIncentiveTierStore = writable<IncentiveTier[]>([]);

export const groupedIncentiveTiers = writable<GroupedTierEntries>([]);

export const getIncentiveTiersStore = async (
  dealId: string,
  dealFlags: DealFeatureFlags,
  active = false
): Promise<void> => {
  if (incentiveTiersEnabled(dealFlags)) {
    const tiers = await getIncentiveTiers(dealId, active);
    incentiveTierStore.set(tiers);
  } else {
    incentiveTierStore.set({items: []});
  }
};

export const groupIncentiveTiers = async (
  dealId: string,
  dealFlags: DealFeatureFlags,
  active = false
): Promise<void> => {
  await getIncentiveTiersStore(dealId, dealFlags, active);

  const tiersToGroup = get(incentiveTierStore).items;
  const groupedTiers = groupBy(tiersToGroup, 'tierType') as GroupedTiers;
  groupedIncentiveTiers.set(Object.entries(groupedTiers) as GroupedTierEntries);
};

const filterPersistedTierEntries = (
  entries: GroupedTierEntries
): GroupedTierEntries => {
  return entries.reduce(
    (accumulator: GroupedTierEntries, [tierType, tiersArray]) => {
      const persistedTiers = tiersArray.filter(
        (tier: IncentiveTier) => tier.id !== null && tier.id !== undefined
      );

      if (persistedTiers.length > 0) {
        accumulator.push([tierType, persistedTiers]);
      }

      return accumulator;
    },
    []
  );
};

const mergeTierEntries = (
  currentCleanedEntries: GroupedTierEntries,
  newGroupedTiers: GroupedTiers
): GroupedTierEntries => {
  const finalMap = new Map<TierType, IncentiveTier[]>(currentCleanedEntries);

  for (const [tierType, newTiersForType] of Object.entries(newGroupedTiers)) {
    const key = tierType as TierType;
    const currentTiersForType = finalMap.get(key) || [];

    const tiersById = new Map<number, IncentiveTier>();

    const allTiersForType = [...currentTiersForType, ...newTiersForType];

    allTiersForType.forEach(tier => {
      if (tier.id !== null) {
        tiersById.set(tier.id, tier);
      }
    });

    finalMap.set(key, Array.from(tiersById.values()));
  }

  const mergedEntries = Array.from(finalMap.entries());
  return mergedEntries;
};

export const updateGroupedIncentiveTiers = (
  newTiers: IncentiveTier[]
): void => {
  if (!newTiers || newTiers.length === 0) {
    return;
  }

  const processedNewTiers = newTiers.map(tier => camelizeKeys(tier));

  const groupedNewTiers = groupBy(
    processedNewTiers,
    'tierType'
  ) as GroupedTiers;

  groupedIncentiveTiers.update(
    (currentEntries: GroupedTierEntries): GroupedTierEntries => {
      const cleanedCurrentEntries: GroupedTierEntries =
        filterPersistedTierEntries(currentEntries);
      const finalEntries: GroupedTierEntries = mergeTierEntries(
        cleanedCurrentEntries,
        groupedNewTiers
      );
      return finalEntries;
    }
  );
};

export const getInvestorIncentiveTierStore = async (
  dealId: number,
  dealFlags: DealFeatureFlags,
  investorId: number
): Promise<void> => {
  if (incentiveTiersEnabled(dealFlags)) {
    const tiers = await getInvestorIncentiveTiers(dealId, investorId);
    investorIncentiveTierStore.set(tiers.items);
  } else {
    investorIncentiveTierStore.set([]);
  }
};

export const dirtyTiersForms = writable(new Set<number>());

export const addDirtyTierForm = (tierId: number) => {
  dirtyTiersForms.update(set => {
    set.add(tierId);
    return set;
  });
};

export const successUpdateTiers = writable(new Set<number>());

export const addSuccessUpdateTier = (tierId: number) => {
  successUpdateTiers.update(set => {
    set.add(tierId);
    return set;
  });
};

export const hasAnOpenTierForm = writable<boolean>(false);

export const hasActiveTiers = derived(
  incentiveTierStore,
  $incentiveTierStore => $incentiveTierStore?.items?.length > 0
);
